import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { authApi, configureClient, session, type Me } from '@/api';
import { queryClient } from './queryClient';
import { WORKSPACES } from './workspaces';
import { purposeForRole } from '@/api/modules/auth';
import { audienceOf, listenSession } from '@/api/sessionSync';

export type AuthStatus = 'restoring' | 'signed-out' | 'signed-in';
/** Session lock keeps the app mounted underneath so the same patient and draft return after re-auth (HF-008). */
export type LockReason = null | 'idle' | 'expired';

interface AuthContextValue {
  status: AuthStatus;
  me: Me | null;
  lock: LockReason;
  /** Step 1: returns the MFA token */
  login: (username: string, password: string) => Promise<string>;
  /** Step 2: completes sign-in. Returns whether this is the same person who was signed in before the lock. */
  verifyMfa: (mfaToken: string, code: string) => Promise<{ sameUser: boolean }>;
  logout: () => Promise<void>;
  switchContext: (roleId: string, locationId?: string | null) => Promise<Me>;
  refreshMe: () => Promise<void>;
  lockNow: (reason: Exclude<LockReason, null>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const RESUME_KEY = 'etnas.resume';
export interface ResumePoint {
  userId: string;
  path: string;
  at: number;
}
export function saveResume(p: ResumePoint) {
  try {
    sessionStorage.setItem(RESUME_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}
export function readResume(): ResumePoint | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    return raw ? (JSON.parse(raw) as ResumePoint) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('restoring');
  const [me, setMe] = useState<Me | null>(null);
  const [lock, setLock] = useState<LockReason>(null);
  const lastUserId = useRef<string | null>(null);
  useEffect(() => {
    const clearIdentity = () => {
      void queryClient.cancelQueries(); queryClient.clear();
      lastUserId.current = null; setMe(null); setLock(null); setStatus('signed-out');
      sessionStorage.removeItem(RESUME_KEY); sessionStorage.removeItem('etnas.slots');
    };
    const unsubscribe = session.subscribe((_snapshot, reason) => { if (reason === 'identity-changed') clearIdentity(); });
    const unlisten = listenSession(event => {
      if (event.action === 'activity') return;
      const current = session.get().user;
      if (event.action === 'signout' || (current && (event.userId !== current.id || event.audience !== audienceOf(current)))) session.clear('identity-changed');
      else if (event.action === 'lock' && current) {
        session.clear('expired');
        setLock(l => l ?? 'expired');
      }
    });
    return () => { unsubscribe(); unlisten(); };
  }, []);

  const applyMe = useCallback((m: Me) => {
    setMe(m);
    lastUserId.current = m.user.id;
    session.setContext({
      tenantId: m.tenant?.id ?? null,
      locationId: m.context.locationId,
      purpose: purposeForRole(m.context.roleId, WORKSPACES[m.context.role.key]?.purpose ?? 'TREATMENT'),
    });
  }, []);

  // Restore via refresh cookie on load.
  useEffect(() => {
    let alive = true;
    (async () => {
      const ok = await authApi.refresh();
      if (!alive) return;
      if (ok) {
        try {
          applyMe(await authApi.me());
          setStatus('signed-in');
          return;
        } catch {
          /* fall through */
        }
      }
      setStatus('signed-out');
    })();
    return () => {
      alive = false;
    };
  }, [applyMe]);

  // The client tells us when refresh fails: lock instead of tearing the app down.
  useEffect(() => {
    configureClient({
      onSessionExpired: () => {
        setLock((l) => l ?? 'expired');
      },
    });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const r = await authApi.login({ username, password });
    if (r.status === 'mfa_required') return r.mfaToken;
    return '';
  }, []);

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const prev = lastUserId.current;
      await authApi.verifyMfa({ mfaToken, code });
      const m = await authApi.me();
      const sameUser = !!prev && prev === m.user.id;
      if (!sameUser) {
        // A different person: never resume someone else's patient or draft.
        queryClient.clear();
      } else {
        queryClient.invalidateQueries();
      }
      applyMe(m);
      setLock(null);
      setStatus('signed-in');
      return { sameUser };
    },
    [applyMe],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* best effort */
    }
    queryClient.clear();
    lastUserId.current = null;
    try {
      sessionStorage.removeItem(RESUME_KEY);
      sessionStorage.removeItem('etnas.slots');
    } catch {
      /* ignore */
    }
    setMe(null);
    setLock(null);
    setStatus('signed-out');
  }, []);

  const switchContext = useCallback(
    async (roleId: string, locationId?: string | null) => {
      const m = await authApi.updateContext({ roleId, locationId });
      // Permissions changed: drop everything cached under the previous role.
      const drafts = queryClient.getQueriesData({ queryKey: ['draft'] });
      queryClient.clear();
      for (const [key, draft] of drafts) queryClient.setQueryData(key, draft);
      applyMe(m);
      return m;
    },
    [applyMe],
  );

  const refreshMe = useCallback(async () => {
    applyMe(await authApi.me());
  }, [applyMe]);

  const lockNow = useCallback((reason: Exclude<LockReason, null>) => {
    // Revoke the server session; the UI stays mounted (blurred, inert) until the same person signs in again.
    authApi.lock().catch(() => {});
    setLock(reason);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, lock, login, verifyMfa, logout, switchContext, refreshMe, lockNow }),
    [status, me, lock, login, verifyMfa, logout, switchContext, refreshMe, lockNow],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const c = useContext(AuthContext);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}

/** Convenience: the signed-in workforce context (throws if absent; use inside protected routes only). */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('useMe used without a session');
  return me;
}

export function useRole() {
  return useMe().context.role.key;
}

export function useCan(perm: string): boolean {
  const { me } = useAuth();
  return !!me?.permissions.includes(perm);
}
